import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Table, Button, Space, Tag, Typography, Card, Modal, App } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

const { Title } = Typography;

const AdminReleases = () => {
    const { message, modal } = App.useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const pageSize = 12;

    const load = async (p = 1) => {
        setLoading(true);
        try {
            const locale = localStorage.getItem('locale') || 'en';
            const res = await axios.get('/v1/admin/releases', { params: { page: p, size: pageSize, locale } });
            setItems(res.data?.data || []);
            setTotal(res.data?.pagination?.total || 0);
            setPage(res.data?.pagination?.page || p);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(1);
    }, []);

    const handleDelete = async (release) => {
        modal.confirm({
            title: 'Delete Release',
            content: (
                <div>
                    <p>Are you sure you want to delete this release?</p>
                    <p><strong>{release.title || release.slug}</strong></p>
                    {release.version && <p>Version: <strong>{release.version}</strong></p>}
                    <p style={{ color: '#ff4d4f', fontSize: '14px' }}>This action cannot be undone.</p>
                </div>
            ),
            okText: 'Delete',
            okType: 'danger',
            cancelText: 'Cancel',
            onOk: async () => {
                try {
                    await axios.delete(`/v1/releases/${release.id}`);
                    message.success('Release deleted successfully');
                    load(page); // Reload current page
                } catch (error) {
                    console.error('Delete error:', error);
                    message.error(`Failed to delete release: ${error.response?.data?.message || error.message}`);
                }
            },
        });
    };

    const columns = [
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (text, record) => <Link to={`/releases/${record.slug}`}>{text || record.slug}</Link>,
        },
        {
            title: 'Version',
            dataIndex: 'version',
            key: 'version',
            render: (v) => v ? <Tag color="blue">v{v}</Tag> : null,
        },
        {
            title: 'Featured',
            dataIndex: 'featured',
            key: 'featured',
            render: (f) => f ? <Tag color="gold">Featured</Tag> : null,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                const colors = {
                    'draft': 'default',
                    'review': 'processing',
                    'scheduled': 'warning',
                    'published': 'success'
                };
                return <Tag color={colors[status] || 'default'}>{(status || 'draft').toUpperCase()}</Tag>;
            },
        },
        {
            title: 'Published',
            dataIndex: 'published_at',
            key: 'published_at',
            render: (d) => d ? new Date(d).toLocaleDateString() : '-',
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            width: 180,
            render: (_, record) => (
                <Space>
                    <Link to={`/admin/releases/${record.slug}/edit`}><Button size="small">Edit</Button></Link>
                    <Button
                        size="small"
                        type="default"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                        title="Delete release"
                    />
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Title level={2} style={{ margin: 0 }}>Admin: Releases</Title>
                    <Link to="/admin/releases/new"><Button type="primary">+ New Release</Button></Link>
                </Space>
                <Card variant="outlined">
                    <Table
                        rowKey={(r) => r.id}
                        loading={loading}
                        columns={columns}
                        dataSource={items}
                        pagination={{ current: page, pageSize, total, onChange: (p) => load(p) }}
                    />
                </Card>
            </Space>
        </div>
    );
};

export default AdminReleases;
